// The one shipped bundle.
//
// The i18n plumbing stays — every string is still reached through `t()` and a
// second locale is a file away — but only English is built. The Slovenian
// bundle was a machine translation nobody had reviewed, and a portal is not a
// place to find that out; every technician who opens this speaks English.
//
// Two rules were applied to the whole file when the UX review went through it:
//
//  1. A string names a value, a state or an action. It does not explain a
//     decision. The explanation belongs in the code comment beside the
//     behaviour and, for the ones worth keeping, in README.md.
//  2. A hint exists only where a field's format is not guessable from the
//     field. `HH:MM-HH:MM` is guessable from a placeholder; "the public half
//     only" is not, so that one stays.
export default {
    general: {
        add:           'Add',
        sign_out:      'Sign out',
        edit:          'Edit',
        cancel:        'Cancel',
        save:          'Save',
        close:         'Close',
        retry:         'Refresh',
        back:          'Back',
        confirm:       'Confirm',
        delete:        'Delete',
        search:        'Search',
        no_matches:    'No matches',
        range:         '{from}–{to} of {total}',
        no_data:       'Nothing here',
        clear_filters: 'Clear',
        never:         'Never',
        yes:           'Yes',
        no:            'No',
        other:         'Other…',
        minutes_ago:   '{count} min ago',
        hours_ago:     '{count} h ago',
        days_ago:      '{count} d ago',
        just_now:      'just now',
    },
    // The sidebar's brand row (vue-spec templates/shell/Sidebar.vue).
    brand: {
        product_name: 'Imprion OS Management',
    },

    layout: {
        open_menu:  'Open menu',
        close_menu: 'Close menu',
    },

    color_mode: {
        switch_to_light: 'Switch to light mode',
        switch_to_dark:  'Switch to dark mode',
    },

    nav: {
        portal:   'Management portal',
        operator: 'Operator',
        menu:     'Menu',
        group_fleet:          'Fleet',
        group_administration: 'Administration',
        fleet:   'Fleet',
        claim:   'Claim',
        clients: 'Clients',
        rollout: 'Rollout',
        logout:  'Sign out',
        alerts:   'Alerts',
        audit:    'Audit',
        ssh_keys: 'Technician keys',
        printers: 'Printers',
        settings:  'Settings',
        operators: 'Operators',
    },
    settings: {
        title:     'Settings',
        theme:     'Theme',
        account:   'Your account',
        operators: 'Accounts',
    },
    auth: {
        sign_in_with_imprion_id: 'Sign in with Imprion ID',
        no_access:               'No access',
        profile:                 'Profile',
        reason:                  {
            no_role:      'No role assigned',
            account_type: 'Account type not allowed',
            refused:      'Access refused',
        },
    },
    device: {
        fleet:              'Fleet',
        // The fleet list's one switch. Label only, per the UI-copy rule: what
        // it hides is named by the word on it, and the badge in the State
        // column says the rest.
        show_retired:       'Show decommissioned',
        terminal:           'Terminal',
        serial:             'Serial',
        hostname:           'Hostname',
        name:               'Name',
        client:             'Client',
        site:               'Site',
        state:              'State',
        wave:               'Wave',
        last_seen:          'Last seen',
        public_ip:          'Public IP',
        claimed_at:         'Claimed',
        agent_version:      'Agent',
        identity:           'Identity',
        inventory:          'Hardware',
        printers:           'Printers',
        versions:           'Versions',
        last_heartbeat:     'Last report',
        no_heartbeat:       'Not reported yet.',
        provisioning:       'Provisioning',
        re_adopted:         'Re-adopted',
        cpu:                'CPU',
        ram:                'RAM',
        disk:               'Disk',
        temperature:        'Temperature',
        uptime:             'Uptime',
        network:            'Network',
        services:           'Services',
        time_synced:        'Clock',
        backend_reachable:  'Backend',
        maintenance_window: 'Maintenance window',
        ssh_keys:           'Applied ssh keys',
        rustdesk_id:        'RustDesk id',
        bios:               'BIOS',
        imprion_release:    'Imprion release',
        kernel:             'Kernel',
        pos_version:        'PoS',
        teleport_version:   'Teleport',
        // The eleven destinations of the device page, as tab labels: one
        // short noun each, in the order the maintenance UI's sidebar uses.
        tab: {
            overview:    'Overview',
            network:     'Network',
            printers:    'Printers',
            touch:       'Touch',
            updates:     'Updates',
            diagnostics: 'Diagnostics',
            backups:     'Backups',
            access:      'Access',
            screen:      'Screen',
            lifecycle:   'Lifecycle',
            audit:       'Audit',
        },
        model:              'Disk model',
        wear:               'wear',
        dns_ok:             'DNS ok',
        dns_failed:         'DNS failed',
        link_down:          'Link down',
        disk_health:        'Disk health',
        rustdesk_version:   'RustDesk',
        jre_version:        'Java',
        metapackage:        'Metapackage',
        base_version:       'Base package',
        guard_version:      'Guard package',
        boot_id:            'Boot id',
        last_apply:         'Last apply',
        last_apply_at:      'Applied at',
        reboot_required:    'Reboot required',
        reboot_scheduled:   'Reboot scheduled',
        // Decision 323: a time zone change the PoS has not been restarted for.
        pos_restart_owed:   'PoS restart owed',
        pos_restart_since:  'PoS restart owed since',
        timezone:           'Time zone',
        // F37/F38. Every bundle the terminal has produced, not only the one
        // this page session collected — and a bundle that was never uploaded
        // is finished, not collecting.
        diagnostics:          'Diagnostics bundles',
        no_bundles:           'No bundle collected.',
        collect_bundle:       'Collect a bundle',
        bundle_collected:     'Collected',
        bundle_uploaded:      'Where',
        bundle_on_server:     'On the server',
        bundle_terminal_only: 'On the terminal only',
        bundle_failed:        'The bundle could not be downloaded',
        // Decision 178. A bundle collected at the till is an audit entry and
        // not a command, so there is nothing for this list to project — and a
        // technician who cannot find the one they just made needs to be told
        // where it is rather than left to collect a second one.
        bundle_local_note:    'A bundle collected at the terminal itself appears only on its maintenance screen.',
        // F34. "No window" is a choice with a consequence, and the consequence
        // is what the hint names: the site's window, then the fleet's (D32).
        window_inherit:       'No window of its own — inherit',
        window_inherit_fleet: 'Fleet default',
        window_source_site:   'Site {site}',
        window_source_device: 'Device',
        window_own:           'Its own window',
        window_from_site:     'This terminal will use its site\'s window: {window}',
        window_from_fleet:    'The fleet-wide default window applies.',
        no_printers:        'No printer reported.',
        ip:                 'IP',
        offline_hint:       'No heartbeat for five cadences',
        updates:            'Updates',
        update_settings:    'Settings',
        settings_saved:     'Saved; applied on the next heartbeat',
        transfer:           'Transfer',
        transfer_title:     'Transfer the terminal',
        transferred:        'Terminal transferred',
        transfer_failed:    'Transfer failed',
        apply_now:          'Apply now',
        pending_updates:    'Pending updates',
        no_pending:         'No pending updates.',
        // Decision 293. The state badge, the update card and the fleet column.
        update_state: {
            unknown:        'Not reported',
            updating:       'Updating',
            failed:         'Update failed',
            reboot_needed:  'Installed, reboot needed',
            not_downloaded: 'Not downloaded',
            downloaded:     'Downloaded',
            up_to_date:     'Up to date',
        },
        update_column:      'Update',
        index_refreshed:    'Index refreshed',
        reboot_since:       'Reboot needed since',
        reboots_at:         'Reboots at',
        last_apply_source:  'Started by',
        apply_source: {
            boot:    'Boot',
            window:  'Maintenance window',
            command: 'Portal',
            local:   'Technician',
        },
        last_apply_error:   'Last error',
        downloaded:         'Downloaded',
        // Decision 294.
        os_version:         'OS version',
        newer_than_cloud:   'Newer than server',
        package:            'Package',
        installed:          'Installed',
        candidate:          'Candidate',
        origin:             'Origin',
        security:           'Security',
        config_version:     'Config version',
        window_inherited:   'From the site',
        window_invalid:     'Use HH:MM-HH:MM',
        no_services:        'No service health reported.',
        healthy:            'Healthy',
        unhealthy:          'Unhealthy',
        restarts:           '{count} restarts',
        touch_devices:      'Touch panels',
        printer_devices:    'Printer devices',
        backups:            'Backups',
        backup_now:         'Back up data',
        no_backups:         'No backup taken.',
        // F64. This list is built from the command queue, so a backup taken at
        // the till is not in it — and "No backup taken." was therefore a true
        // sentence about the server and a false one about the terminal. These
        // two lines are what the empty card could not say. The counts line is
        // drawn only when the terminal reported some; the note below it is
        // always there, in the same voice and the same place as the
        // diagnostics card's equivalent (README decision 178).
        backups_on_terminal: 'Kept on the terminal: {count} (newest {when}). On the server: {sent}.',
        backup_local_note:   'A backup taken at the terminal stays there until somebody sends it, and appears here only once it has.',
        backup_size:        'Size',
        backup_taken:       'Taken',
        backup_files:       'Contents',
        backup_download:    'Download',
        backup_failed:      'The backup could not be downloaded',
        summary: {
            total:       'Terminals',
            online:      'Online',
            offline:     'Offline',
            alerting:    'Alerting',
            open_alerts: '{count} open',
            out_of_date: 'Behind',
            canary:      'Canary',
            stable:      '{count} stable',
        },
        lifecycle: {
            unclaimed:      'Unclaimed',
            // A re-adopt, waiting for the terminal to come back with a code.
            pending:        'Awaiting re-enrolment',
            claimed:        'Claimed',
            active:         'Active',
            offline:        'Offline',
            decommission_pending: 'Decommission pending',
            decommissioned: 'Decommissioned',
            revoked:        'Revoked',
        },
    },
    // The device page's Network tab (Doc 2 §6.1 "network state"). Every word
    // here is nmcli's, because that is what the agent reads the picture out of
    // and inventing a second vocabulary for the same states would only make a
    // technician on the phone and an operator on the portal disagree.
    network: {
        title:         'Interfaces',
        interface:     'Interface',
        type:          'Type',
        state:         'State',
        ipv4:          'IPv4',
        ipv6:          'IPv6',
        mac:           'MAC',
        gateway:       'Gateway',
        dns:           'DNS',
        method:        'Method',
        primary_ipv4:  'Primary IPv4',
        ssid:          'Wi-Fi network',
        reported_at:   'Reported',
        // The empty state is the empty state. The two paragraphs that used to
        // stand under it — why the list can be missing, and what the summary
        // card below does carry — were explanation, and the UI copy rule cuts
        // explanation.
        none:          'No interfaces reported.',
        link:          'Link',
        link_up:       'Up',
        link_down:     'Down',
        backend:       'Management server',
        backend_ok:    'Reachable',
        backend_fail:  'Unreachable',
        dns_ok:        'Resolving',
        dns_fail:      'Not resolving',
        wifi_signal:   'Wi-Fi signal',
        summary:       'Last heartbeat',
    },
    printer: {
        name:       'Queue',
        device_uri: 'Device URI',
        certified:  'Certified',
        // F87. The lone "Port bound" badge named the flag, and its absence —
        // the better case, a queue named after the printer's serial — said
        // nothing. Both values now name the identifier.
        bound_serial: 'Serial number',
        // F91: a serial-less printer's queue is identified by its VID:PID and
        // no longer by the socket it happens to be in.
        bound_identity: 'VID:PID',
        bound_model:    'Model',
        rejecting:    'Rejecting jobs',
        queued_jobs: 'queued',
        default:    'Default',
        // A remote test page's own answer (F44), in the same words the
        // maintenance UI uses at the till so a technician on the phone to an
        // operator is reading the same sentence.
        test_result_title:  'Test page sent to {name}',
        test_result_bytes:  '{bytes} bytes handed to CUPS.',
        test_result_queue:  'Queue is {state} with {jobs} job(s) waiting.',
        test_result_reason: 'CUPS says: {reason}',
        state: {
            idle:       'Idle',
            processing: 'Printing',
            stopped:    'Stopped',
            offline:    'Offline',
        },
        // The three transports (F90). `USB` is CUPS' own backend and keeps the
        // plain name because it is the ordinary case; the other two say which
        // of the two unusual ones it is. The same three words are on the
        // maintenance UI at the till.
        transport: {
            cups_usb:    'USB',
            imprion_usb: 'Imprion USB',
            serial:      'Serial',
        },
        transport_column: 'Transport',
        paper_width:      'Paper width',
        unsupported:      'Unsupported',
        // Add USB printer (batch 5): the maintenance UI's "Add other", remote.
        // The same labels the till shows.
        usb: {
            add:               'Add USB printer',
            empty:             'No devices found.',
            list_failed:       'Could not read the USB devices',
            add_failed:        'Could not add the printer',
            added:             'Printer "{name}" added',
            reload:            'Reload',
            vendor_product:    'VID:PID',
            serial:            'Serial',
            usb_port:          'USB port',
            interface:         'Interface',
            endpoint:          'Endpoint',
            endpoints:         'Endpoints',
            endpoint_packet:   '{bytes} B',
            no_bulk_out:       'No bulk-OUT',
            in_registry:       'In registry',
            name:              'Queue name',
            out_endpoint:      'Out endpoint',
            interface_number:  'Interface',
            alternate_setting: 'Alternate setting',
            validation_name:     'Letters, digits, - and _',
            validation_endpoint: '0x and two hex digits',
            validation_byte:     'A number from 0 to 255',
        },
    },
    provisioning: {
        step: {
            jwt_persisted:     'Identity stored',
            apt_sources:       'Apt suite written',
            ssh_keys:          'Technician keys written',
            totp:              'Maintenance secret stored',
            pos:               'PoS installed',
            teleport:          'Teleport joined',
            rustdesk:          'RustDesk configured',
            rustdesk_assigned: 'RustDesk assigned',
            marker:            'Enrolment marker written',
        },
        state: {
            pending: 'Waiting',
            running: 'Running',
            done:    'Done',
            skipped: 'Skipped',
            failed:  'Failed',
        },
        attempts: 'attempt | attempts',
        // The step an enrolling terminal is on, for the fleet row and the
        // device header (batch 5).
        current: {
            jwt_persisted:     'Identity',
            apt_sources:       'Apt suite',
            ssh_keys:          'Technician keys',
            totp:              'Maintenance secret',
            pos:               'PoS',
            teleport:          'Teleport',
            rustdesk:          'RustDesk',
            rustdesk_assigned: 'RustDesk assignment',
            marker:            'Enrolment marker',
        },
        status: {
            waiting:   'Waiting for claim · {code}',
            enrolling: 'Enrolling · {done}/{total} · {step}',
            failed:    'Enrollment failed · {step}',
        },
    },
    enrollment: {
        claim:            'Claim a terminal',
        claim_action:     'Claim',
        claim_hint:       'Scan the QR or type the code.',
        code:             'Join code',
        code_placeholder: 'ABCD1234',
        resolve:          'Look up',
        scan:             'Scan',
        stop_scan:        'Stop',
        scan_unsupported: 'This browser cannot scan; type the code.',
        scan_failed:      'The camera could not be opened.',
        not_found:        'No terminal is waiting on that code.',
        expired:          'That code has expired.',
        already_claimed:  'That code has already been used.',
        open_codes:       'Codes on a screen right now',
        confirm_title:    'Claim this terminal',
        readopt_title:    'Re-adopt this terminal',
        readopt_hint:     'Known serial — the existing record is reused.',
        pick_client:      'Client',
        pick_site:        'Site',
        new_client:       'New client',
        new_site:         'New site',
        claimed:          'Terminal claimed',
        claim_failed:     'The claim did not go through',
        expires:          'Expires',
        last_polled:      'Last asked',
        state: {
            pending:  'Waiting',
            claimed:  'Claimed',
            expired:  'Expired',
            rejected: 'Rejected',
        },
    },
    client: {
        clients:       'Clients',
        client:        'Client',
        name:          'Name',
        alias:         'Alias',
        tax_number:    'Tax number',
        timezone:      'Time zone',
        source:        'Source',
        state:         'State',
        synced_at:     'Synced',
        source_manual: 'Manual',
        source_pos:    'PoS',
        pos_disabled:  'Disabled',
        pos_deleted:   'Deleted',
        link:          'Link to PoS',
        link_client:   'PoS client',
        link_site:     'PoS site',
        linked:        'Linked',
        business_unit_code:      'Code',
        street:                  'Street',
        house_number:            'House number',
        house_number_additional: 'Addition',
        postal_code:             'Postal code',
        city:                    'City',
        community:               'Community',
        cadastral_number:        'Cadastral number',
        building_number:         'Building number',
        building_section_number: 'Building section',
        add:           'Add a client',
        edit:          'Edit the client',
        sites:         'Sites',
        add_site:      'Add a site',
        site_name:     'Name',
        address:       'Address',
        terminals:     'Terminals',
        edit_site:     'Edit the site',
        no_window:     'None',
        confirm_delete: 'Delete this client? Only a client with no terminals can be deleted.',
    },
    pos_sync: {
        title:    'PoS sync',
        off:      'Off',
        ok:       'OK',
        error:    'Error',
        running:  'Running',
        never:    'Never',
        last_run: 'Last run',
        clients:  'Clients',
        sites:    'Sites',
        changes:  'Changes',
    },
    // The device page's action triggers and their confirmations (decision 333).
    action: {
        terminal:       'Terminal',
        printer:        'Printer',
        service:        'Service',
        window:         'Window',
        window_24h:     '24 h',
        tty:            'Serial port',
        baud:           'Baud rate',
        name:           'Queue name',
        reason:         'Reason',
        power:          'Power',
        reboot:         'Reboot',
        shutdown:       'Shut down',
        test_page:      'Print test page',
        set_default:    'Set default',
        clear_jobs:     'Clear jobs',
        remove_printer: 'Remove printer',
        add_serial:     'Add serial printer',
        restart:        'Restart',
        restart_service: 'Restart service',
        network_test:   'Network test',
        network_none:   'No test yet.',
        collect_bundle: 'Collect bundle',
        apply_updates:  'Apply updates',
        config_apply:   'Apply configuration',
        calibration:    'Start calibration',
        backup:         'Back up data',
        rotate:         'Rotate password',
        de_enroll:      'De-enroll',
        decommission:   'Decommission',
        revoke:         'Revoke',
        reinstate:      'Reinstate',
        cancel_command: 'Cancel action',
    },
    command: {
        sent:               'Sent',
        dispatch_failed:    'The action was not queued',
        result:             'Result',
        request_id:         'Request id',
        cancel:             'Cancel',
        cancelled:          'The action was cancelled',
        cancel_failed:      'The action could not be cancelled',
        download_bundle:    'Download ({size})',
        download_failed:    'The bundle could not be downloaded',
        decommission_reason: 'Decommissioned from the portal',
        type: {
            printer_test_page:   'Print a test ticket',
            printer_set_default: 'Set as default printer',
            printer_remove:      'Remove the printer',
            printer_clear_jobs:  'Clear stuck jobs',
            printer_add_serial:  'Add a serial printer',
            printer_list_usb:    'List USB devices',
            printer_add_usb:     'Add a USB printer',
            printer_set_paper_width: 'Set paper width',
            calibration_start:   'Start calibration',
            service_restart:     'Restart a service',
            reboot:              'Reboot',
            shutdown:            'Shut down',
            diagnostics_bundle:  'Collect a log bundle',
            network_test:        'Run a network test',
            config_apply:        'Apply configuration',
            update_apply:        'Apply updates',
            pos_backup:          'Back up data',
            de_enroll:           'De-enroll',
            decommission:        'Decommission',
            screen_capture:      'Capture the screen',
            maintenance_ticket:  'Maintenance panel login',
        },
        state: {
            queued:    'Queued',
            delivered: 'Sent',
            finished:  'Done',
            failed:    'Failed',
            expired:   'Expired',
            cancelled: 'Cancelled',
        },
    },
    alert: {
        title:            'Alerts',
        none:             'Nothing is wrong anywhere.',
        open_only:        'Open only',
        open_for_device:  'Open alerts',
        opened:           'Opened',
        acknowledge:      'Acknowledge',
        acknowledged:     'Acknowledged',
        ack_note:         'Note',
        severity_column:  'Severity',
        type_column:      'Alert',
        severity: {
            info:     'Info',
            warning:  'Warning',
            critical: 'Critical',
        },
        state: {
            open:         'Open',
            acknowledged: 'Acknowledged',
            resolved:     'Resolved',
        },
        type: {
            heartbeat_missed: 'Heartbeat missed',
            disk_full:        'Disk full',
            disk_wearing:     'Disk wearing out',
            printer_error:    'Printer error',
            printer_offline:  'Printer offline',
            time_drift:       'Clock drift',
            time_unsynced:    'Clock not synchronised',
            thermal:          'Running hot',
            failed_update:    'Update failed',
            guard_activated:  'Guard activated',
            service_failed:   'Service failed',

            screen_cast_unavailable: 'Screen sharing unavailable',
            rustdesk_drift:          'RustDesk config drift',

            release_drift:              'Release drift',
            release_archive_unreadable: 'aptly unreachable',
        },
    },
    rollout: {
        title:              'Rollout',
        no_snapshot:        'Nothing recorded yet',
        no_versions:        'No version reported yet.',
        no_snapshots:       'No snapshot recorded.',
        release_log:        'Release log',
        snapshot:           'Snapshot',
        published_to:       'Wave',
        published_at:       'Published',
        notes:              'Notes',
        soak:               'Soak',
        soak_hours:         '{hours} h',
        soak_until:         'until {when}',
        soak_closed:        'Closed',
        budget:             'budget {budget}',
        budget_spent:       '{used} of {budget} spent',
        budget_label:       'Error budget',
        terminals_label:    'Terminals',
        terminals:          '{count} terminals',
        converged:          'Converged',
        applied_since:      'Applied since switch',
        reboot_pending:     'Reboot pending',
        open_alerts:        'Open alerts',
        guard_activations:  'Guard activations',
        failed_applies:     'Failed applies',
        pause:              'Pause',
        resume:             'Resume',
        apply_now:          'Apply to this wave',
        assign:             'Assign wave',
        assign_title:       'Move terminals between waves',
        assigned:           '{count} terminals moved',
        assign_failed:      'The wave assignment failed',
        any_client:         'Any client',
        any_site:           'Any site',
        pick_terminals:     'Pick terminals',
        paused_by_operator: 'Paused from the portal',
        no_reason:          'No reason recorded.',
        confirm_resume:     'Resume {wave}',
        reason:             'Reason',
        pause_failed:       'The wave could not be paused',
        resume_failed:      'The wave could not be resumed',
        fanout_queued:      '{queued} terminals queued, {remaining} left',
        fanout_failed:      'The apply could not be dispatched',
        // F35. Every switch on the releases page records itself; this repairs
        // a suite changed outside it, read from aptly. One key per label.
        reconcile:          'Reconcile',
        reconciled:         '{count} suites recorded from aptly',
        reconcile_in_sync:  'In sync with aptly',
        reconcile_failed:   'aptly unreachable',
        // F52. The convergence bar covers the packages the fleet reports; these
        // are the ones in the target set that nothing reports a version for.
        unmeasured:         'Not measured',
        wave: {
            stable: 'Stable',
            canary: 'Canary',
        },
        details:            'Details',
        history:            'History',
        history_count:      'No publications | {count} publication | {count} publications',
        online:             'Online',
        drift:              'Drift',
        recorded_as:        'Recorded',
        served_packages:    'Packages',
        update_states:      'Update state',
        state: {
            idle:    'Idle',
            soaking: 'Soaking',
            soaking_until: 'Soaking until {when}',
            settled: 'Settled',
            paused:  'Paused',
            halted:  'Halted',
        },
        failure: {
            failed_apply:       'Failed apply',
            guard_downgrade:    'Guard downgrade',
            post_apply_offline: 'Silent after apply',
            alert_raised:       'New alert',
        },
        // Keyed on the Debian package name since F52 — the archive's spelling
        // is the one both sides compare on. A package with no friendly name of
        // its own falls through to the key itself (packageLabel).
        package: {
            'imprion-pos':             'imprion-pos',
            'imprion-pos-agent':       'imprion-pos-agent',
            'imprion-pos-agent-guard': 'imprion-pos-agent-guard',
            'imprion-pos-base':        'imprion-pos-base',
            'default-jre-headless':    'Java',
            pos:                       'PoS',
            imprion_release:           'Imprion release',
            teleport:                  'Teleport',
            rustdesk:                  'RustDesk',
            kernel:                    'Kernel',
        },
    },
    releases: {
        title:             'Releases',
        upload:            'Upload',
        uploaded:          '{version} uploaded',
        upload_failed:     'Upload failed',
        load_failed:       'Load failed',
        off:               'Repository off',
        suites:            'Suites',
        no_releases:       'No releases',
        no_packages:       'No packages',
        not_published:     'Not published',
        snapshot:          'Snapshot',
        release:           'Release',
        version:           'Version',
        uploaded_at:       'Uploaded',
        uploaded_by:       'Uploaded by',
        packages:          'Packages',
        package:           'Package',
        state_label:       'State',
        size:              'Size',
        sha256:            'SHA-256',
        git_rev:           'Git revision',
        tag:               'Tag',
        previous_tag:      'Previous tag',
        built_at:          'Built',
        min_cloud_version: 'Min. cloud version',
        extension_version: 'Back-to-PoS extension',
        key_fingerprints:  'Archive keys',
        commits:           'Commits',
        canary_at:         'On canary',
        stable_at:         'On stable',
        git_dirty:         'Dirty tree',
        lab:               'Lab build',
        publish_canary:    'Publish to canary',
        promote_stable:    'Promote to stable',
        rollback:          'Roll back {suite}',
        switch_title:      'Switch {suite} to {snapshot}',
        switch:            'Switch',
        switch_failed:     'Switch failed',
        switch_result:     '{suite}: {snapshot}',
        preview_failed:    'Preview failed',
        first_publish:     'First publish',
        no_downgrade:      'Tills do not downgrade',
        current:           'Current',
        new:               'New',
        changed:           'Changed',
        recorded:          'Recorded',
        not_recorded:      'Not recorded',
        unchanged:         'Unchanged',
        warning: {
            guard_changed:     'Guard changed',
            min_cloud_version: 'Below min. cloud version',
            not_on_canary:     'Not on canary',
            older_release:     'Older release',
        },
        state: {
            not_published: 'Not published',
            deleted:       'Deleted',
        },
        repository:        'Repository',
        status:            'Status',
        aptly_version:     'aptly',
        repo:              'Repo',
        prefix:            'Prefix',
        signing_key:       'Signing key',
        storage_free:      'Free',
        storage_total:     'Total',
        storage_used:      'Used',
        snapshots:         'Snapshots',
        repo_refs:         'Package refs',
        upload_dirs:       'Upload leftovers',
        keep:              'Keep releases',
        last_retention:    'Last retention',
        no_retention:      'No run yet',
        run_retention:     'Run retention',
        retention_done:    'Retention done',
        retention_skipped: 'Retention skipped',
        retention_failed:  'Retention failed',
        finished_at:       'Finished',
        trigger_label:     'Trigger',
        dropped:           'Dropped snapshots',
        removed_refs:      'Removed refs',
        storage_change:    'Storage used',
        snapshots_change:  'Snapshots',
        duration:          'Duration',
        error:             'Error',
        skipped:           'Skipped',
        repo_state: {
            ready:       'Ready',
            not_ready:   'Not ready',
            unreachable: 'Unreachable',
        },
        trigger: {
            ticker:   'Hourly',
            operator: 'Operator',
        },
    },
    audit: {
        title:       'Audit',
        when:        'When',
        action:      'Action',
        detail:      'Detail',
        operator:    'Operator',
        system:      'System',
        download:    'Download',
        download_failed: 'Download failed.',
        capture_expired: 'Expired',
        actions: {
            device_claimed:       'Terminal claimed',
            device_readopted:     'Terminal re-adopted',
            device_updated:       'Terminal edited',
            device_moved:         'Terminal moved',
            device_decommission:  'Decommission requested',
            device_decommissioned: 'Decommission completed',
            device_de_enrolled:   'Terminal de-enrolled',
            device_offline:       'Terminal went offline',
            device_revoked:       'Identity revoked',
            device_reinstated:    'Terminal reinstated',
            client_created:       'Client created',
            site_created:         'Site created',
            command_enqueued:     'Action dispatched',
            command_cancelled:    'Action cancelled',
            command_finished:     'Action finished',
            command_expired:      'Action expired',
            calibration_recorded: 'Calibration recorded',
            rustdesk_password_rotated: 'RustDesk password rotated',
            rustdesk_drift:       'RustDesk config drift',
            ssh_key_added:        'Technician key added',
            ssh_key_removed:      'Technician key revoked',
            totp_rotated:         'Maintenance secret rotated',
            rollout_paused:       'Wave paused',
            rollout_resumed:      'Wave resumed',
            snapshot_promoted:    'Snapshot promoted',
            release_uploaded:     'Release uploaded',
            release_refused:      'Release refused',
            release_switched:     'Release switched',
            repository_retention: 'Repository retention',
            alert_acknowledged:   'Alert acknowledged',
            alert_raised:         'Alert raised',
            local_maintenance:    'Local maintenance',
            operator_created:     'Operator created',
            operator_disabled:    'Operator disabled',
            operator_enabled:     'Operator enabled',
            operator_password_changed: 'Password changed',
            screen_capture_downloaded: 'Screen capture downloaded',
            device_jwt_recovered: 'Token recovered',
            alert_resolved:       'Alert resolved',
            client_renamed:       'Client renamed',
            client_linked:        'Client linked',
            site_renamed:         'Site renamed',
            site_linked:          'Site linked',
            pos_sync:             'PoS sync',
            printer_registry_added:   'Registry printer added',
            printer_registry_updated: 'Registry printer edited',
            printer_registry_removed: 'Registry printer removed',
            printer_pending_dismissed: 'Pending printer dismissed',
        },
    },
    ssh_key: {
        title:          'Technician keys',
        none:           'No key registered.',
        add:            'Add a key',
        name:           'Name',
        owner:          'Owner',
        public_key:     'Public key',
        public_key_hint: 'The public half only, one line.',
        fingerprint:    'Fingerprint',
        expires:        'Expires',
        active:         'Active',
        expired:        'Expired',
        revoked:        'Revoked',
        revoke:         'Revoke',
        revoked_toast:  'Revoked; the fleet converges on the next heartbeat',
        confirm_revoke: 'Revoke this key? {owner} loses ssh access to every terminal.',
        invalid:        'That does not look like an ssh public key',
    },
    // F90. The fleet-wide printer registry and its inbox. Labels and values:
    // the page says what a row is, never why it is there.
    printer_registry: {
        title:        'Printer registry',
        none:         'No printers registered.',
        add:          'Add a printer',
        edit:         'Edit',
        delete:       'Delete',
        name:         'Name',
        vendor_id:    'Vendor ID',
        product_id:   'Product ID',
        transport:    'Transport',
        interface:    'Interface',
        // The three "which pipe" overrides (F90). One column in the table,
        // three fields in the form.
        out_endpoint:      'Out endpoint',
        interface_number:  'Interface',
        alternate_setting: 'Alternate setting',
        auto_add:     'Auto-add',
        added:        'Added',
        queue_name_template: 'Queue name template',
        notes:        'Notes',
        interface_class:    'Interface class',
        interface_subclass: 'Subclass',
        interface_protocol: 'Protocol',
        confirm_delete: 'Delete "{name}"?',
        deleted:        'Deleted',
        load_failed:    'Could not read the registry',
        // Four-hex-digit ids, two-hex-digit class fields, `0x` plus two for an
        // endpoint: the server's own Valid(), said as the shape it wants.
        validation_id:        'Four hex digits',
        validation_hex:       'Two hex digits',
        validation_endpoint:  '0x and two hex digits',
        validation_byte:      'A number from 0 to 255',
    },
    printer_pending: {
        title:        'Pending',
        none:         'No pending printers.',
        manufacturer: 'Manufacturer',
        product:      'Product',
        queue:        'Queue',
        terminal:     'Terminal',
        first_seen:   'First seen',
        last_seen:    'Last seen',
        promote:      'Add to registry',
        confirm_promote: 'Add {vendor}:{product} to the registry?',
        confirm_delete:  'Delete this pending printer?',
        deleted:         'Deleted',
        load_failed:     'Could not read the pending list',
    },
    errors: {
        generic_error:  'An error occurred',
        invalid_data:   'Invalid data',
        not_found:      'Not found',
        unauthorized:   'Unauthorized',
        conflict:       'That is not possible in the current state',
        already_exists: 'A record with this name already exists',
        expired:        'That has expired',
        on_add:         'Error while adding',
        on_edit:        'Error while updating',
        on_delete:      'Error while deleting',
        forbidden:      'Not allowed',
    },
    success: {
        add:    'Successfully added',
        edit:   'Successfully updated',
        delete: 'Successfully deleted',
    },
    validation: {
        required:   'Required',
        email:      'Invalid email address',
        code:       'A join code is 8 characters',
        max_length: 'At most {max} characters',
    },
    remote: {
        title:                   'Remote access',
        connect:                 'Connect to {id}',
        copy_link:               'Copy the link',
        copied:                  'Copied',
        rustdesk_pending:        'No RustDesk id reported.',
        teleport_not_configured: 'Teleport is not configured.',
        teleport_pending:        'No Teleport node reported.',
        open_node:               'Open terminal',
        rotate:                  'Rotate password',
        rotate_failed:           'The password was not rotated',
        screen_cast_ready:       'Ready',
        screen_cast_unavailable: 'Unavailable',
        screen_cast_unreported:  'Not reported',
        maintenance:             'Maintenance panel',
        maintenance_open:        'Open',
        maintenance_no_address:  'No LAN address.',
        maintenance_checking:    'Checking',
        maintenance_reachable:   'Reachable',
        maintenance_same_network: 'Same network',
        maintenance_unconfirmed: 'Not confirmed',
    },
    screen: {
        title:     'Screen',
        refresh:   'Refresh',
        empty:     'No capture yet.',
        failed:    'Capture failed.',
        expired:   'Terminal offline.',
        captured:  'Captured',
        size:      'Size',
        source:    'Source',
        missing:   'Image unavailable.',
    },
    calibration: {
        title:           'Touch calibration',
        never:           'Never calibrated.',
        start:           'Start calibration',
        result:          'Result',
        passed:          'Verified',
        failed:          'Failed',
        panel:           'Panel',
        when:            'Calibrated',
        matrix:          'libinput matrix',
        inverted:        'Negative scale — the panel is mounted the other way up.',
    },
    lifecycle: {
        title:                     'Identity and end of life',
        config_version:            'Config version',
        revoke:                    'Revoke now',
        revoked:                   'Identity revoked',
        revoke_failed:             'The identity was not revoked',
        reason:                    'Reason',
        decommission_pending:      'Decommission pending',
        decommissioned:            'Decommissioned',
        re_adopted:                'Re-adopted after a reinstall',
        // The way back from the two banners above. One word and one question:
        // the UI copy rule cuts the paragraph that would explain what a
        // denylist is, and decision 219 cuts the typed hostname.
        reinstate:                 'Reinstate',
        reinstate_failed:          'The terminal was not reinstated',
    },
    operator: {
        title:        'Operators',
        name:         'Name',
        email:        'Email',
        last_sign_in: 'Last sign-in',
        deleted:      'Deleted user',
        role_label:   'Role',
        role: {
            admin:    'Admin',
            operator: 'Operator',
        },
    },
    // Settings → Technician authenticator (D30, Doc 2 §6). One secret for the
    // whole fleet; the page is where a technician's phone gets it.
    totp: {
        title:            'Technician authenticator',
        subtitle:         'One code opens the maintenance UI on every terminal in the fleet.',
        scan_hint:        'Scan this with an authenticator app, or type the key below into it.',
        secret:           'Setup key',
        show_secret:      'Show the key',
        hide_secret:      'Hide the key',
        copy_secret:      'Copy',
        copied:           'Setup key copied',
        copy_failed:      'The key could not be copied; select it and copy it by hand',
        parameters:       'Parameters',
        parameters_value: '{algorithm}, {digits} digits, every {period} s',
        previous:         'Previous key',
        previous_active:  'Valid until the next rotation',
        previous_none:    'None',
        rotate:           'Rotate the key',
        rotated:          'Rotated; every terminal applies it on its next heartbeat',
        rotate_failed:    'The key was not rotated',
        rotated_at:       'Last rotated',
        never_rotated:    'Never',
        device_count:     'Terminals it applies to',
        // The rotation is not destructive, but it does retire the key before
        // the current one, so a technician whose phone is two rotations behind
        // stops being able to sign in. Typing the word is the pause that buys.
        confirm_word:     'ROTATE',
        confirm_label:    'Type {word} to confirm',
        confirm_hint:     'Type {word} exactly.',
        confirm_body:     'A new key is generated and pushed to every terminal. The key in force now stays valid until the next rotation; the one before it stops working immediately.',
    },
}
